import React, { useState } from 'react';
import { notification } from 'antd';
import { FiPlay, FiPause } from 'react-icons/fi';
import { useMutation } from '@tanstack/react-query';
import { getCallRecording } from '@/api/logs';
import { useAudioPlayer } from 'src/contexts/AudioPlayerContext';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getAgentById } from '@/store/account/selector';
import { ECallRecordingType } from 'src/enums/ECallRecording';

interface AudioPlayerProps {
	callSid: string;
	duration?: string;
	isRecorded?: boolean;
	type: string;
	voicemailRecordStarted?: number;
	voicemailRecordDuration?: number;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({
	callSid,
	isRecorded,
	type,
	voicemailRecordStarted,
	voicemailRecordDuration,
}) => {
	const [recordingUrls, setRecordingUrls] = useState<Record<string, string>>({});
	const [loadingRecording, setLoadingRecording] = useState(false);
	const currentAgent = useAppSelector(getAgentById);

	const recordingUrl = recordingUrls[callSid] || null;

	const { playAudio, pauseAudio, isPlaying, currentAudioUrl, currentRecord } = useAudioPlayer();

	const callRecordingMutation = useMutation<{ callSid: string; url: string }, Error, string>({
		mutationFn: getCallRecording,
		onMutate: () => {
			setLoadingRecording(true);
		},
		onSuccess: (data) => {
			setRecordingUrls((prev) => ({ ...prev, [callSid]: data.url }));
			setLoadingRecording(false);
			handlePlayPause(data.url);
		},
		onError: () => {
			notification.error({ message: 'Failed to load recording' });
			setLoadingRecording(false);
		},
	});

	const handleGetCallRecording = () => {
		callRecordingMutation.mutate(callSid);
	};

	const handlePlayPause = (audioUrl: string) => {
		if (!audioUrl) {
			return;
		}

		let playType: ECallRecordingType.CALL | ECallRecordingType.VOICEMAIL = ECallRecordingType.CALL;
		if (type === ECallRecordingType.CALL || type === ECallRecordingType.VOICEMAIL) {
			playType = type;
		}

		const recordData = { callSid, voicemailRecordStarted, voicemailRecordDuration };
		const shouldPause = isPlaying && currentAudioUrl === audioUrl;

		if (shouldPause) {
			pauseAudio();
		} else {
			playAudio(audioUrl, recordData, playType);
		}
	};

	const isCurrentlyPlaying = isPlaying && currentRecord?.callSid === callSid;

	return (
		<div className="flex items-center justify-between py-3 px-4 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow mb-4">
			{isRecorded ? (
				<div className="flex items-center gap-3">
					<div
						onClick={() => {
							if (recordingUrl) {
								handlePlayPause(recordingUrl);
							} else {
								handleGetCallRecording();
							}
						}}
						className={`w-10 h-10 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 ${
							loadingRecording
								? 'bg-gray-400 cursor-not-allowed'
								: isCurrentlyPlaying
									? 'bg-red-500 hover:bg-red-600 shadow-lg'
									: 'bg-blue-500 hover:bg-blue-600 shadow-lg'
						}`}
					>
						{loadingRecording ? (
							<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
						) : isCurrentlyPlaying ? (
							<FiPause className="text-white w-4 h-4" />
						) : (
							<FiPlay className="text-white w-4 h-4 ml-0.5" />
						)}
					</div>

					<div className="flex flex-col">
						<span className="text-sm font-medium text-gray-800">Call Recording</span>
						<span className="text-xs text-gray-500">
							{isCurrentlyPlaying ? 'Playing in audio player' : 'Click to play'}
						</span>
					</div>
				</div>
			) : (
				<div className="flex flex-col">
					<span className="text-sm font-medium text-red-800">No Recording Found</span>
					{currentAgent.call_recording_enabled === false && (
						<span className="text-xs text-gray-500">Recording not enabled</span>
					)}
				</div>
			)}

			<div className="flex items-center gap-2">
				{isCurrentlyPlaying && (
					<div className="flex items-center gap-1">
						<div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
						<span className="text-xs text-red-500 font-medium">LIVE</span>
					</div>
				)}
			</div>
		</div>
	);
};

export default AudioPlayer;
